import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  AssetHeader,
  EmptyState,
  PrimaryButton,
  SecondaryButton,
  TextButton,
  StatusPill,
  type StatusLevel,
} from '@/lib';
import { goAsset } from '@/app/navigation';
import { Gated } from '@/app/CapabilitiesProvider';
import type { ApiClients } from '@/api/client';
import {
  useAlertDetail,
  useAlertExplanation,
  useAcknowledgeAlert,
  useSnoozeAlert,
  useResolveAlert,
  type EvidenceEntry,
} from './queries';
import { AlertComments } from './AlertComments';

export type S4AlertDetailProps = {
  alertId: string;
  clients: ApiClients;
};

const SEVERITY_TO_LEVEL: Record<string, StatusLevel> = {
  critical: 'critical',
  warn: 'warn',
  watch: 'watch',
  healthy: 'healthy',
};

const EVIDENCE_TONE_CLASS: Record<string, string> = {
  critical: 'text-critical',
  warn: 'text-warn-fg',
  watch: 'text-watch-fg',
  healthy: 'text-healthy-fg',
  neutral: 'text-gray-900',
};

/**
 * S4 Alert Detail. Phase 4 (4/n). Composes the AssetHeader band variant
 * + the "why this fired" explanation + the alert action panel (ack,
 * snooze, resolve-as-FP, assign deferred to Phase 4 (5..N)).
 *
 * Optimistic mutation handling is intentionally light here — the alert
 * lifecycle has stronger backend invariants than S1's queue, so we
 * just invalidate on success and surface error inline.
 */
export function S4AlertDetail({ alertId, clients }: S4AlertDetailProps) {
  const navigate = useNavigate();
  const alert = useAlertDetail(clients, alertId);
  const explanation = useAlertExplanation(clients, alertId);
  const ack = useAcknowledgeAlert(clients);
  const snooze = useSnoozeAlert(clients);
  const resolve = useResolveAlert(clients);

  const [actor] = useState('engineer@example.com'); // Phase 5 (RBAC) reads from auth claims

  if (alert.isLoading) {
    return <S4Loading />;
  }
  if (alert.isError || !alert.data) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load alert"
        body={String(alert.error ?? 'alert detail failed')}
        cta={{ label: 'Retry', onClick: () => alert.refetch() }}
      />
    );
  }

  const a = alert.data;
  const severityLevel: StatusLevel = SEVERITY_TO_LEVEL[a.severity] ?? 'watch';
  const statusLabel = a.status === 'acknowledged' ? 'Acknowledged' : a.status === 'resolved' ? 'Resolved' : 'Open';
  const isOpen = a.status === 'open';

  return (
    <div className="flex flex-col gap-sp-6">
      <AssetHeader
        asset={{
          id: a.assetId,
          name: a.assetName,
          klass: '—',
          site: '—',
          criticality: 'high',
          health: 0,
          thresholds: { warn: 70, critical: 30 },
        }}
        freshness={{ at: new Date(a.createdAt), tone: 'fresh' }}
        variant="band"
        alert={{ level: severityLevel, label: statusLabel }}
      />

      <section
        aria-labelledby="alert-title"
        className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-6"
      >
        <div className="flex flex-wrap items-center gap-sp-3">
          <StatusPill level={severityLevel} label={a.severity.toUpperCase()} />
          <span className="font-mono text-fs-meta text-gray-700">{a.alertId}</span>
        </div>
        <h1 id="alert-title" className="mt-sp-3 text-fs-h1 font-medium text-gray-900">
          {a.title}
        </h1>
        <p className="mt-sp-3 text-fs-body text-gray-900">{a.summary}</p>

        <div className="mt-sp-4 flex flex-wrap items-center gap-sp-3">
          {/* Acknowledge — every authenticated role can ack per ADR 0010.
              No <Gated> wrapper needed; the capability is universal. */}
          <PrimaryButton
            label={ack.isPending ? 'Acknowledging…' : 'Acknowledge'}
            disabled={!isOpen || ack.isPending}
            onClick={() => ack.mutate({ alertId, actor })}
          />
          {/* Snooze — Maintainer+. Viewers don't see the buttons at all. */}
          <Gated capability="alert.snooze">
            <SecondaryButton
              label={snooze.isPending ? 'Snoozing…' : 'Snooze 24h'}
              disabled={a.status === 'resolved' || snooze.isPending}
              onClick={() => snooze.mutate({ alertId, duration: '24h' })}
            />
            <SecondaryButton
              label="Snooze 7d"
              disabled={a.status === 'resolved' || snooze.isPending}
              onClick={() => snooze.mutate({ alertId, duration: '7d' })}
            />
          </Gated>
          {/* Resolve as false positive — Maintainer+. */}
          <Gated capability="alert.resolve">
            <TextButton
              label={resolve.isPending ? 'Resolving…' : 'Resolve as false positive'}
              disabled={a.status === 'resolved' || resolve.isPending}
              onClick={() =>
                resolve.mutate({ alertId, actor, reason: 'false_positive' })
              }
            />
          </Gated>
          <TextButton
            label="Open asset"
            onClick={() => goAsset(navigate, a.assetId)}
          />
        </div>

        {ack.isError ? (
          <p role="alert" className="mt-sp-3 text-fs-meta text-critical">
            Acknowledge failed. Retry from the button.
          </p>
        ) : null}
        {snooze.isError ? (
          <p role="alert" className="mt-sp-3 text-fs-meta text-critical">
            Snooze failed. Retry from the button.
          </p>
        ) : null}
        {resolve.isError ? (
          <p role="alert" className="mt-sp-3 text-fs-meta text-critical">
            Resolve failed. Retry from the button.
          </p>
        ) : null}
      </section>

      <section aria-labelledby="explanation-heading" className="flex flex-col gap-sp-3">
        <h2
          id="explanation-heading"
          className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
        >
          Why this fired
        </h2>
        {explanation.isLoading ? (
          <p className="text-fs-body text-gray-700">Loading explanation…</p>
        ) : explanation.isError || !explanation.data ? (
          <p className="text-fs-body text-gray-700" role="alert">
            Explanation unavailable.
          </p>
        ) : (
          <>
            <p className="text-fs-body text-gray-900">{explanation.data.narrative}</p>
            {(explanation.data.evidence ?? []).length > 0 ? (
              <ul className="flex flex-col gap-sp-2">
                {(explanation.data.evidence ?? []).map((e: EvidenceEntry, i) => (
                  <li
                    key={`${e.label}-${i}`}
                    className="grid grid-cols-[8rem_1fr_auto] items-center gap-sp-3 rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2"
                  >
                    <span className="text-fs-meta text-gray-700">{e.label}</span>
                    <span className={`text-fs-body ${EVIDENCE_TONE_CLASS[e.tone] ?? 'text-gray-900'}`}>
                      {e.value}
                    </span>
                    <span className="text-fs-meta uppercase tracking-wider text-gray-700">
                      {e.tone}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-fs-meta text-gray-700">No evidence rows yet.</p>
            )}
            {explanation.data.snoozed_until ? (
              <p className="text-fs-meta text-gray-700">
                Snoozed until{' '}
                <time dateTime={explanation.data.snoozed_until}>
                  {new Date(explanation.data.snoozed_until).toLocaleString()}
                </time>
                .
              </p>
            ) : null}
          </>
        )}
      </section>

      <AlertComments alertId={alertId} />

      {a.recommendations && a.recommendations.length > 0 ? (
        <section aria-labelledby="recs-heading" className="flex flex-col gap-sp-3">
          <h2
            id="recs-heading"
            className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
          >
            Recommended actions
          </h2>
          <ul className="flex flex-col gap-sp-2">
            {a.recommendations.map((r, i) => (
              <li
                key={i}
                className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2 text-fs-body text-gray-900"
              >
                {r}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function S4Loading() {
  return (
    <div className="flex flex-col gap-sp-6" aria-busy="true">
      <div className="h-20 animate-pulse rounded-r-card bg-gray-100" />
      <div className="h-40 animate-pulse rounded-r-card bg-gray-100" />
      <div className="h-32 animate-pulse rounded-r-card bg-gray-100" />
    </div>
  );
}
